import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** One photo as the slideshow endpoint returns it. */
export interface SlideshowPhoto {
  id: string;
  photoUrl: string;
  mediaType?: 'photo' | 'video';
  submittedAt: string;
  submittedBy: string | null;
  teamId: string;
  teamName: string;
  challengeId: string;
  challengeName: string;
  /** Dropped from the reel. The team keeps its points either way. */
  slideshowExcluded: boolean;
  /** Pinned to the front; null means "wherever chronology puts it". */
  slideshowOrder: number | null;
}

export const SLIDESHOW_PHOTOS_QUERY_KEY = 'EventSlideshowPhotos';

/**
 * Photos for the slideshow, already in the order they'll play.
 *
 * The same endpoint the projector display reads, so the curation panel
 * and the big screen can never disagree about the running order.
 */
export function useSlideshowPhotos(eventId: string | undefined) {
  const apiClient = useApiClient();
  return useQuery<{ success: boolean; data: { photos: SlideshowPhoto[] } }>({
    queryKey: [SLIDESHOW_PHOTOS_QUERY_KEY, eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/photos`),
    enabled: Boolean(eventId),
    staleTime: 10_000,
  });
}

export interface CuratePhotoInput {
  submissionId: string;
  excluded?: boolean;
  order?: number | null;
}

/**
 * Include, exclude or feature one photo.
 *
 * Refetches rather than patching the cache by hand: the running order is
 * computed server-side, so featuring one photo can move others, and
 * guessing at the new order in the client would drift from the projector.
 */
export function useCuratePhoto(eventId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, ...body }: CuratePhotoInput) =>
      apiClient.patch(`/events/${eventId}/photos/${submissionId}/slideshow`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SLIDESHOW_PHOTOS_QUERY_KEY, eventId] });
    },
  });
}
